/* eslint-disable max-lines */
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { WarehouseETADetailsModel } from './WarehouseETADetailsModel';
import { Warehouse } from '../internal/common.types';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { WarehouseETAOutOfStock } from './WarehouseETAOutOfStock';
import { useTranslate } from '@im/react-localization';
import WarehouseETAVendorBHODirectShip from './internalETA/WarehouseETAVendorBHO_DS';
import WarehouseETAStockOverMouse from './internalETA/WarehouseETAStockOverMouse';
import Popover from '@mui/material/Popover';

type Props = {
  centralStock: number | false;
  centralStockJType: number | false;
  displayCentralFlag: boolean;
  displayLocalFlag: boolean;
  displayOutOfStockFlag: boolean;
  etaEllipsis: boolean;
  fromPage: string;
  licenseSelectorVendorSubrange?: string;
  localStock: number | false;
  onOrder: number | false;
  preOrderEtaStockDetails: any;
  reserveFlag: boolean;
  reservedStock: number | false;
  showWarehousePopupflag: boolean;
  sku: string;
  skuType?: string;
  stockStatus: string;
  totalAvailableStock: number;
  vendorCode?: string;
  warehouseStockDetail: any;
  warehouses?: Warehouse[];
};

export function WarehouseETADisplayText(props: Props) {
  const {
    centralStock,
    centralStockJType,
    displayCentralFlag,
    displayLocalFlag,
    displayOutOfStockFlag,
    etaEllipsis,
    fromPage,
    licenseSelectorVendorSubrange,
    localStock,
    onOrder,
    preOrderEtaStockDetails,
    reserveFlag,
    reservedStock,
    showWarehousePopupflag,
    sku,
    skuType,
    stockStatus,
    vendorCode,
    warehouseStockDetail,
    warehouses,
  } = props;

  const [showWarehouseDetails, setShowWarehouseDetails] = useState(
    showWarehousePopupflag
  );

  // eslint-disable-next-line import/no-named-as-default-member
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (etaEllipsis) setAnchorEl(event.currentTarget);
    else setAnchorEl(null);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const translate = useTranslate();

  function getNumV1(val: any) {
    if (isNaN(val)) {
      return 0;
    }
    return val;
  }

  return (
    <>
      <Typography
        bgcolor={
          getNumV1(localStock) > 0 ||
          getNumV1(centralStock) > 0 ||
          getNumV1(centralStockJType) > 0 ||
          getNumV1(reservedStock) > 0 ||
          stockStatus?.toUpperCase() !== 'OUTOFSTOCK'
            ? '#E3FFFA'
            : '#FEF3F2'
        }
        component="span"
        noWrap
        sx={{
          alignItems: 'center',
          borderRadius: '16px',
          //display: 'flex',
          gap: '8px',
          lineHeight: '16px',
          padding: '4px 8px',
        }}
        variant="inherit"
      >
        <StyledButton
          data-testid={'WarehouseETADisplayText'}
          onClick={() => {
            getNumV1(localStock) > 0 ||
            getNumV1(centralStock) > 0 ||
            getNumV1(centralStockJType) > 0 ||
            getNumV1(reservedStock) > 0 ||
            displayOutOfStockFlag
              ? setShowWarehouseDetails(true)
              : setShowWarehouseDetails(false);
          }}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          size="small"
          sx={{
            color: displayOutOfStockFlag ? '#B11616' : '#054F31',
            cursor:
              getNumV1(localStock) > 0 ||
              getNumV1(centralStock) > 0 ||
              getNumV1(centralStockJType) > 0 ||
              getNumV1(reservedStock) > 0 ||
              displayOutOfStockFlag
                ? 'pointer'
                : 'initial',
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: '16px',
            padding: '0px',
            textTransform: 'none',
          }}
          variant="text"
        >
          <Stack
            alignItems="center"
            direction="row"
            divider={
              Boolean(displayLocalFlag) &&
              Boolean(displayCentralFlag) && (
                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{
                    borderLeftWidth: '0px',
                    borderRightWidth: '1px',
                    color: 'red',
                    margin: '0px',
                  }}
                />
              )
            }
            justifyContent="center"
            sx={{
              '.MuiDivider-root': { borderColor: '#054F31' },
              alignSelf: 'inherit',
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '16px',
            }}
          >
            {displayLocalFlag ? (
              <Box sx={{ display: 'flex' }}>
                {displayLocalFlag ? (
                  <div
                    style={{
                      marginRight:
                        (displayLocalFlag && displayCentralFlag) ||
                        (reserveFlag && displayCentralFlag)
                          ? '0.50rem'
                          : '',
                      maxWidth: etaEllipsis ? '65px' : 'auto',
                      overflow: etaEllipsis ? 'hidden' : 'none',
                      textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                      whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontSize: '12px', fontWeight: '600' }}
                    >
                      {localStock}
                    </Typography>{' '}
                    <Typography
                      component="span"
                      sx={{ fontSize: '12px', fontWeight: '400' }}
                    >
                      {translate('Warehouse.ETALocalShipmentText')}{' '}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: '12px', fontWeight: '600' }}
                    >
                      {reserveFlag
                        ? `(${reservedStock} ${translate(
                            'Cart.Reserved'
                          )})${' '}`
                        : ''}
                    </Typography>
                  </div>
                ) : (
                  ''
                )}
              </Box>
            ) : (
              ''
            )}
            {reserveFlag && displayCentralFlag ? (
              <Box sx={{ display: 'flex' }}>
                {reserveFlag &&
                displayCentralFlag &&
                !displayLocalFlag &&
                !displayOutOfStockFlag ? (
                  <>
                    <div
                      style={{
                        marginRight:
                          reserveFlag && displayCentralFlag ? '0.50em' : '',
                        maxWidth: etaEllipsis ? '65px' : 'auto',
                        overflow: etaEllipsis ? 'hidden' : 'none',
                        textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                        whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ fontSize: '12px', fontWeight: '600' }}
                      >
                        {reservedStock}
                      </Typography>{' '}
                      {translate('Cart.Reserved')}{' '}
                      {translate('Warehouse.ETALocalShipmentText')}
                    </div>
                    {reserveFlag && displayCentralFlag && (
                      <hr
                        className="MuiDivider-root MuiDivider-fullWidth MuiDivider-vertical MuiDivider-flexItem css-h7zdi2-MuiDivider-root"
                        data-testid="required"
                      />
                    )}
                  </>
                ) : (
                  ''
                )}
              </Box>
            ) : (
              ''
            )}
            {displayCentralFlag ? (
              <Box sx={{ display: 'flex' }}>
                {displayCentralFlag && getNumV1(centralStock) > 0 ? (
                  <div
                    style={{
                      marginLeft:
                        (displayLocalFlag && displayCentralFlag) ||
                        (reserveFlag && displayCentralFlag)
                          ? '0.50rem'
                          : '',
                      maxWidth: etaEllipsis ? '65px' : 'auto',
                      overflow: etaEllipsis ? 'hidden' : 'none',
                      textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                      whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                    }}
                  >
                    {' '}
                    <Typography
                      component="span"
                      sx={{ fontSize: '12px', fontWeight: '600' }}
                    >
                      {centralStock}
                    </Typography>{' '}
                    {translate('Warehouse.ETACentrlShipmentText')}
                  </div>
                ) : (
                  ''
                )}
              </Box>
            ) : (
              ''
            )}

            {displayCentralFlag && getNumV1(centralStockJType) > 0 ? (
              <Box sx={{ display: 'flex' }}>
                {displayCentralFlag && getNumV1(centralStockJType) > 0 ? (
                  <div
                    style={{
                      marginLeft:
                        (displayLocalFlag && displayCentralFlag) ||
                        (reserveFlag && displayCentralFlag)
                          ? '0.50rem'
                          : '',
                      maxWidth: etaEllipsis ? '65px' : 'auto',
                      overflow: etaEllipsis ? 'hidden' : 'none',
                      textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                      whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                    }}
                  >
                    {' '}
                    <Typography
                      component="span"
                      sx={{ fontSize: '12px', fontWeight: '600' }}
                    >
                      {centralStockJType}
                    </Typography>{' '}
                    {translate('Warehouse.ETACentralJTypeShipmentText')}
                  </div>
                ) : (
                  ''
                )}
              </Box>
            ) : (
              ''
            )}

            {reserveFlag && !displayCentralFlag && !displayLocalFlag ? (
              <Box>
                {reserveFlag && !displayCentralFlag && !displayLocalFlag ? (
                  <div
                    style={{
                      maxWidth: etaEllipsis ? '65px' : 'auto',
                      overflow: etaEllipsis ? 'hidden' : 'none',
                      textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                      whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                    }}
                  >
                    {reserveFlag
                      ? `${reservedStock} ${translate(
                          'Cart.Reserved'
                        )}${' '}${translate(
                          'Warehouse.ETALocalShipmentText'
                        )}${' '}`
                      : ''}
                  </div>
                ) : (
                  ''
                )}
              </Box>
            ) : (
              ''
            )}
          </Stack>
          {!reserveFlag &&
            !displayCentralFlag &&
            !displayLocalFlag &&
            stockStatus?.toLowerCase() !== 'directship' &&
            stockStatus?.toLowerCase() !== 'direct_ship' &&
            getNumV1(onOrder) > 0 && (
              <WarehouseETAOutOfStock
                centralStock={centralStock}
                etaEllipsis={etaEllipsis}
                localStock={localStock}
                preOrderEtaStockDetails={preOrderEtaStockDetails}
                reserveFlag={props.reserveFlag}
                tooltipsShow={false}
                warehouses={warehouses}
              ></WarehouseETAOutOfStock>
            )}
          {!reserveFlag &&
            !displayCentralFlag &&
            !displayLocalFlag &&
            !displayOutOfStockFlag &&
            (stockStatus?.toLowerCase() === 'directship' ||
              stockStatus?.toLowerCase() === 'direct_ship') && (
              <WarehouseETAVendorBHODirectShip
                fromPage={fromPage}
                stockStatus={stockStatus}
                warehouseStockDetail={warehouseStockDetail}
              ></WarehouseETAVendorBHODirectShip>
            )}
          {!reserveFlag &&
            !displayCentralFlag &&
            !displayLocalFlag &&
            displayOutOfStockFlag &&
            (getNumV1(onOrder) < 0 || onOrder === 0 || onOrder === false) &&
            stockStatus?.toLowerCase() === 'outofstock' && (
              <Typography
                color={displayOutOfStockFlag ? '#B11616' : '#054F31'}
                component="span"
                data-id={'OutOfStockOnly'}
                sx={{
                  borderRadius: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: '16px',
                }}
                variant="body2"
              >
                <div
                  style={{
                    maxWidth: etaEllipsis ? '65px' : 'auto',
                    overflow: etaEllipsis ? 'hidden' : 'none',
                    textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                    whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                  }}
                >
                  {fromPage === 'cart'
                    ? translate(`Cart.${stockStatus}`)
                    : translate(`Common.${stockStatus}`)}
                </div>
              </Typography>
            )}

          {!reserveFlag &&
            !displayCentralFlag &&
            !displayLocalFlag &&
            !displayOutOfStockFlag &&
            (getNumV1(onOrder) <= 0 || onOrder === false) &&
            stockStatus?.toLowerCase() !== 'directship' &&
            stockStatus?.toLowerCase() !== 'direct_ship' &&
            stockStatus?.toLowerCase() !== 'outofstock' && (
              <Typography
                color={displayOutOfStockFlag ? '#B11616' : '#054F31'}
                component="span"
                data-id={'OtherOutOfStock'}
                sx={{
                  borderRadius: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: '16px',
                }}
                variant="body2"
              >
                <div
                  style={{
                    maxWidth: etaEllipsis ? '65px' : 'auto',
                    overflow: etaEllipsis ? 'hidden' : 'none',
                    textOverflow: etaEllipsis ? 'ellipsis' : 'none',
                    whiteSpace: etaEllipsis ? 'nowrap' : undefined,
                  }}
                >
                  {fromPage === 'cart'
                    ? translate(`Cart.${stockStatus}`)
                    : translate(`Common.${stockStatus}`)}
                </div>
              </Typography>
            )}

          {etaEllipsis && (
            <Popover
              PaperProps={{
                style: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
              }}
              anchorEl={anchorEl}
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
              }}
              disableRestoreFocus
              id="mouse-over-popover"
              onClose={handlePopoverClose}
              open={open}
              sx={{
                borderRadius: '6px',
                pointerEvents: 'none',
              }}
              transformOrigin={{
                horizontal: 'left',
                vertical: 'top',
              }}
            >
              {anchorEl !== null ? (
                <WarehouseETAStockOverMouse
                  centralStock={centralStock ? centralStock : 0}
                  centralStockJType={centralStockJType ? centralStockJType : 0}
                  displayCentralFlag={displayCentralFlag}
                  displayLocalFlag={displayLocalFlag}
                  displayOutOfStockFlag={displayOutOfStockFlag}
                  fromPage={fromPage}
                  localStock={localStock ? localStock : 0}
                  preOrderEtaStockDetails={preOrderEtaStockDetails}
                  reserveFlag={reserveFlag}
                  reservedStock={reservedStock ? reservedStock : 0}
                  stockStatus={stockStatus}
                  tooltipsShow={open}
                  warehouses={warehouses}
                ></WarehouseETAStockOverMouse>
              ) : (
                ''
              )}
            </Popover>
          )}
        </StyledButton>

        {showWarehouseDetails && (
          <WarehouseETADetailsModel
            displayOutOfStockFlag={displayOutOfStockFlag}
            fromPage={fromPage}
            id={'testid'}
            licenseSelectorVendorSubrange={licenseSelectorVendorSubrange}
            onClose={() => setShowWarehouseDetails(false)}
            onOrderValue={onOrder}
            open={showWarehouseDetails}
            reserveFlag={reserveFlag}
            reservedStock={reservedStock ? reservedStock : 0}
            sku={sku}
            skuType={skuType}
            stockStatus={stockStatus}
            vendorCode={vendorCode}
            warehouseDetails={warehouseStockDetail ?? []}
            warehouses={warehouses}
          />
        )}
      </Typography>
    </>
  );
}

const StyledButton = styled(Button)({
  '&.MuiButton-root:focus': {
    outline: 'none',
  },
  '&.MuiButton-root:hover': {
    background: 'none',
  },
  '&.MuiButtonBase-root, &.MuiButton-root': {
    height: '16px',
    textTransform: 'none',
  },
});
